import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";
import { Add } from "@material-ui/icons";
import Breadcrumb from "../../../components/BreadCrumb";
import Pagination from "../../../components/commons/Pagination";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import ComplaintFilters from "./components/ComplaintFilters";
import ComplaintTable from "./components/ComplaintTable";
import ComplaintDetailDrawer from "./components/ComplaintDetailDrawer";
import CreateCustomerServiceEventModal from "./components/CreateCustomerServiceEventModal";
import { DEFAULT_FILTERS } from "./constants";
import { extractCtripEvents, mapCtripEvents } from "./mapCtripEvents";
import { getThunkErrorMessage, isThunkError } from "./ctripReplyHelpers";
import { toastMessage } from "../../../helpers/toastMessage";
import ThunksRateModule from "../../../redux/actions/thunks/rateModule";
import "./components/complaintManagement.css";

const ITEMS_PER_PAGE = 10;

const buildEventQueryParams = (filters, page, limit) => {
  const params = { page, limit };

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params[key] = String(value).trim();
    }
  });

  return params;
};

const ComplaintManagementPage = () => {
  const dispatch = useDispatch();
  const {
    integrationType,
    integrationId,
    country,
    countryId,
    city,
    cityId,
    airportId,
    airportIATA,
  } = useParams();

  const [complaints, setComplaints] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const secondaryPaths = [
    {
      name: country,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}`,
    },
    {
      name: city,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/city`,
    },
    {
      name: "Airports",
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/city-airport`,
    },
    {
      name: `Manage ${airportIATA}`,
      url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/airport/${airportIATA}/${airportId}/mange-location`,
    },
    { name: "Complaints" },
  ];

  const fetchComplaints = useCallback(
    async (page = 1, limit = ITEMS_PER_PAGE) => {
      if (!airportId) {
        return { items: [], total: 0 };
      }

      try {
        setLoading(true);
        const params = buildEventQueryParams(filters, page, limit);
        const response = await dispatch(
          ThunksRateModule.getEventCtripThunk(airportId, params),
        );

        if (isThunkError(response)) {
          throw new Error(
            getThunkErrorMessage(response, "Failed to fetch complaints"),
          );
        }

        const events = extractCtripEvents(response);
        const mapped = mapCtripEvents(events);
        const total = response?.total ?? mapped.length;

        return {
          items: mapped,
          total,
        };
      } catch (error) {
        toastMessage("error", error?.message || "Failed to fetch complaints");
        return { items: [], total: 0 };
      } finally {
        setLoading(false);
      }
    },
    [airportId, dispatch, filters, listRefreshKey],
  );

  const handlePageChange = useCallback((items) => {
    setComplaints(items);
  }, []);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleView = (complaint) => {
    setSelectedComplaint(complaint);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedComplaint(null);
  };

  const handleComplaintUpdate = (updated) => {
    setSelectedComplaint(updated);
    setComplaints((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c)),
    );
  };

  const handleCreateSuccess = () => {
    setCurrentPage(1);
    setListRefreshKey((key) => key + 1);
  };

  return (
    <div>
      <div className="mt-18 flex justify-content-between">
        <div>
          <Breadcrumb
            paths={[
              {
                name: "Complaints",
                url: `/dashboard/integration/rates/${integrationType}/${integrationId}/${country}/${countryId}/${city}/${cityId}/airport/${airportIATA}/${airportId}/complaint-management`,
              },
            ]}
            toolTipStatus={true}
          />
          <div className="breadcrumb-description">
            Manage and resolve customer complaints effectively.
          </div>
        </div>
        <div className="flex">
          <MOButtonV2
            text="CREATE CS EVENT"
            textColor="#ffffff"
            radius={4}
            backgroundColor="#29AEE6"
            height={30}
            fontSize={11}
            padding="6.5px 24px"
            IconType={() => <Add style={{ marginRight: 8, fontSize: 16 }} />}
            onClick={() => setCreateModalOpen(true)}
          />
        </div>
      </div>

      <div className="mt-12 ml-6">
        <Breadcrumb paths={secondaryPaths} secondary={true} />
      </div>

      <div className="bg-white mt-20 border-radius-8">
        <ComplaintFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
        <ComplaintTable
          complaints={complaints}
          onView={handleView}
          loading={loading}
        />
        <div
          style={{
            backgroundColor: "white",
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <Pagination
            fetchData={fetchComplaints}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            onCurrentPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      <ComplaintDetailDrawer
        open={drawerOpen}
        complaint={selectedComplaint}
        onClose={handleDrawerClose}
        onUpdate={handleComplaintUpdate}
      />

      <CreateCustomerServiceEventModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default ComplaintManagementPage;
